import { chain, maxBy } from 'lodash';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AddProjectDomainDialog from '../../components/muiLegacy/Dialogs/AddDomainDialog/AddProjectDomainDialog';
import KeywordTable from '../../components/muiLegacy/Tables/KeywordTable/KeywordTable';
import {
  KeywordTableColumn,
  KeywordTableColumnTypes,
} from '../../components/muiLegacy/Tables/KeywordTable/KeywordTableColumnPicker/models';
import { useGetDebouncedKeywords } from '../../components/muiLegacy/Tables/KeywordTable/logic/useGetDebouncedKeywords';
import { useKeywordTableState } from '../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import GetRankDataDialog from '../../components/voxel/Dialogs/GetRankDataDialog';
import VoxelVerticalDrawer from '../../components/voxel/Drawers/VoxelVerticalDrawer';
import { DomainTypeGq } from '../../graphql/projects/models/DomainTypeGq';
import { GoogleAdsRequestGq } from '../../graphql/projects/models/GoogleAdsRequestGq';
import { KeywordFilteringGq } from '../../graphql/projects/models/KeywordFilteringGq';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { TagGroupGq } from '../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import { CurrentUserGq } from '../../graphql/users/models/CurrentUserGq';
import { useDebounceValue } from '../../logic/otherUtilities/useDebounceValue';
import ProjectAppBar from '../ProjectPage/ProjectAppBar/ProjectAppBar';
import { useProjectPageTitle } from '../ProjectPage/useProjectPageTitle';
import DomainSection from './DomainSection/DomainSection';
import EditCompetitorsForm from './DomainSection/EditCompetitorsForm';
import ConflictsDialog from './TagSection/Dialogs/AutoConflictsDialog/ConflictsDialog';
import TagGroupSection from './TagSection/TagGroupSection';
import TagSectionBeta from './TagSectionBeta/TagSectionBeta';
import { paramsToKeywordFilter } from './logic/paramsToKeywordFilter';
import { useBulkTagKeywords } from './logic/useBulkTagKeywords';
import { useBulkUntagKeywords } from './logic/useBulkUntagKeywords';

export interface DataManagementPageProps {
  projectId: number;
  project: ProjectGq;
  user: CurrentUserGq;
  reload: () => Promise<unknown>;
  fetchRankData?: boolean;
  pollInterval: number;
}

export interface ViewAnalysisData {
  domainId?: string;
  rankMin?: string;
  rankMax?: string;
  tagGroupId?: string;
  tagId?: string;
}

const DataManagementPage: React.FC<DataManagementPageProps> = ({
  projectId,
  project: rootProject,
  user,
  reload,
  fetchRankData,
  pollInterval,
}) => {
  const project = rootProject;
  useProjectPageTitle('Manage', project);

  // Google ads requests
  const requestsQuery = projectsApiService.queries.googleAds.useGetGoogleAdsRequests(
    { projectId },
    { pollInterval: pollInterval }
  );

  const googleAdsRequestAnalysis = requestsQuery?.result?.analyzedRequests;
  const apiCollections = useMemo(() => {
    const result: Record<number, GoogleAdsRequestGq> = {};
    googleAdsRequestAnalysis.forEach((acc) => (result[acc.request.id] = acc.request));
    return result;
  }, [googleAdsRequestAnalysis]);

  // Download menu
  const [downloadOpen, setDownloadOpen] = useState(false);
  const handleOpenDownloads = useCallback(() => setDownloadOpen(true), []);

  // Autotag conflicts
  const [conflictsTagGroup, setConflictsTagGroup] = useState<TagGroupGq | null>(null);

  // Stored column State
  const columnStateKey = `data-management-column-${projectId}`;
  const storedColumnState = useMemo(() => {
    const storedState = sessionStorage.getItem(columnStateKey);
    if (!storedState?.length) return;
    try {
      return JSON.parse(storedState) as KeywordTableColumn[];
    } catch {
      return undefined;
    }
  }, []);

  // Initial table state
  const initialColumnState: KeywordTableColumn[] = [
    { type: KeywordTableColumnTypes.Keyword, order: 0 },
    { type: KeywordTableColumnTypes.Volume, order: 1 },
    { type: KeywordTableColumnTypes.VolumeTrend, order: 2 },
    {
      type: KeywordTableColumnTypes.EnglishTranslation,
      order: project.hasEnglishTranslation ? 3 : undefined,
    },
    {
      type: KeywordTableColumnTypes.TranslationFrom,
      order: project.translated ? 5 : undefined,
    },
    {
      type: KeywordTableColumnTypes.TranslationFromVolume,
      order: project.translated ? 6 : undefined,
    },
    ...project?.domains
      .filter((domain) => domain.type === DomainTypeGq.PROJECT)
      .map((domain, index) => ({
        type: KeywordTableColumnTypes.Rank,
        id: domain.id,
        order: index + 10,
      })),
    ...project?.tagGroups.slice(0, 1).map((group, index) => ({
      type: KeywordTableColumnTypes.TagGroup,
      id: group.id,
      order: index + 20,
    })),
  ];

  // Params filter
  const paramsFilter = useMemo(() => {
    return paramsToKeywordFilter(window.location.search);
  }, [window.location.search]);

  // Column state
  const searchParamsColumnState: KeywordTableColumn[] = useMemo(() => {
    const result: KeywordTableColumn[] = [];
    let currentColumnOrder = maxBy(initialColumnState, (x) => x.order)?.order;

    paramsFilter?.domainRankFilters.forEach((dr) =>
      result.push({
        type: KeywordTableColumnTypes.Rank,
        id: dr.domainId,
        order: currentColumnOrder++,
      })
    );

    chain(paramsFilter?.tagFilters)
      .groupBy((g) => g.tagGroupId)
      .map((k, v) => v)
      .forEach((tagGroupId) =>
        result.push({
          type: KeywordTableColumnTypes.TagGroup,
          id: +tagGroupId,
          order: currentColumnOrder++,
        })
      )
      .value();

    if (result.length) return [...initialColumnState, ...result];
    else return result;
  }, [paramsFilter]);

  // Final column state
  let columnState = initialColumnState;
  if (searchParamsColumnState?.length) columnState = searchParamsColumnState;
  else if (storedColumnState?.length) columnState = storedColumnState;

  // Set initial filter state
  const initialFilterState = useMemo(() => {
    // Base filter
    const filter: Partial<KeywordFilteringGq> & { projectId: number } = {
      archived: false,
      projectId: projectId,
      ...paramsFilter,
    };

    return filter;
  }, [paramsFilter]);

  // Setup keyword table state
  const keywordTableState = useKeywordTableState(
    columnState,
    initialFilterState,
    { invertSelection: false, keywordIds: [], emptyIsAll: false },
    {
      enableColor: !!project.domains?.length,
      shownTagPanelId: project?.tagGroups[0]?.id,
    },
    project?.domains,
    project?.useAutomaticDomains,
    project?.tagGroups,
    undefined,
    project.translated,
    project.hasEnglishTranslation
  );

  // Set color as enabled if the number of domains have changed
  useEffect(
    () =>
      keywordTableState.setTableUIState((prev) => ({
        ...prev,
        enableColor: !!project.domains?.length,
      })),
    [!!project.domains?.length]
  );

  const debouncedColumnState = useDebounceValue(keywordTableState.columnPickerBag.columnState, 500);
  useEffect(() => {
    let modifiedState = [...debouncedColumnState];
    const stringifiedState = JSON.stringify(modifiedState);
    sessionStorage.setItem(columnStateKey, stringifiedState);
  }, [debouncedColumnState]);

  // Table data
  const domainIds = useMemo(
    () =>
      keywordTableState.columnPickerBag.columnState
        .filter((c) => c.type === KeywordTableColumnTypes.Rank && !!c.order && c.id)
        .map((c) => c.id),
    [keywordTableState.columnPickerBag.columnState]
  );
  const {
    keywords,
    totalCount,
    totalVolume,
    page,
    query: keywordQuery,
  } = useGetDebouncedKeywords(
    keywordTableState,
    { pollInterval: pollInterval },
    undefined,
    domainIds
  );

  // Rank ctr values
  const rankCtrQuery = projectsApiService.queries.useGetCtrValues({ dummy: true });
  const rankCtrResult = !rankCtrQuery.query.loading ? rankCtrQuery?.result : [];
  const ctrValueDictionary = useMemo(
    () => Object.fromEntries(rankCtrResult?.map((item) => [item.rank, item.ctr])),
    [rankCtrResult?.length]
  );

  // Stats
  const selectedKeywordStats = projectsApiService.queries.useGetSelectedKeywordsStats(
    {
      projectId: project.id,
      keywordQuery: {
        filtering: keywordTableState.backendFilterState,
        selecting: { ...keywordTableState.backendSelectionState, emptyIsAll: true },
      },
    },
    { pollInterval: pollInterval }
  );

  const tagGroupStats = projectsApiService.queries.useGetTagGroupStats(
    { projectId: projectId },
    { pollInterval: pollInterval }
  );

  // Autotag Conflicts
  const autotagConflictTagGroups = projectsApiService.queries.useGetTagGroupsWithConflicts(
    { projectId: projectId },
    { pollInterval: pollInterval }
  );

  const combinedReload = useCallback(() => {
    // This will block
    const blockingRefetch = [keywordQuery.refetch()];

    // These won't block
    selectedKeywordStats.query.refetch();
    tagGroupStats.query.refetch();
    autotagConflictTagGroups.query.refetch();
    reload();

    return Promise.all(blockingRefetch);
  }, [
    selectedKeywordStats.query.refetch,
    tagGroupStats.query.refetch,
    keywordQuery.refetch,
    autotagConflictTagGroups.query.refetch,
    reload,
  ]);

  // Actions
  const bulkTagKeywords = useBulkTagKeywords(keywordTableState, combinedReload);
  const bulkUntagKeywords = useBulkUntagKeywords(keywordTableState, combinedReload);
  const setShownTagGroups = useCallback((ids: number[]) => {
    keywordTableState.columnPickerBag.setColumnState((prev) => {
      // Order counter
      const oldMaxOrder = maxBy(prev, (c) => c.order)?.order ?? 0;
      let currentMaxOrder = oldMaxOrder;

      // Update existing tag groups
      const next = prev.map((column) => {
        if (column.type === KeywordTableColumnTypes.TagGroup) {
          const shouldShowExisting = ids.includes(column.id);
          if (shouldShowExisting && column.order === undefined)
            return { ...column, order: currentMaxOrder++ };
          if (!shouldShowExisting && column.order !== undefined) {
            return { ...column, order: undefined };
          }
          return column;
        }

        return column;
      });

      // Add missing tag group
      ids.forEach((id) => {
        if (!next.some((c) => c.type === KeywordTableColumnTypes.TagGroup && c.id === id)) {
          next.push({ type: KeywordTableColumnTypes.TagGroup, id, order: currentMaxOrder++ });
        }
      });

      return next;
    });
  }, []);

  const loaded = project;
  const drawerOpen =
    keywordTableState.tableUIState.showTagPanel || keywordTableState.tableUIState.showDomainsPanel;

  // Add domain
  const [addDomainOpen, setAddDomainOpen] = useState<DomainTypeGq | null>(null);

  // Get rank data
  const [openGetRanksDialog, setOpenGetRanksDialog] = useState(fetchRankData ? true : false);

  return (
    <div className="fixed w-full h-full">
      <ProjectAppBar
        project={project}
        user={user}
        drawerOpen={drawerOpen}
        downloadOpen={downloadOpen}
      />
      {loaded && <div className={'h-14'} />} {/* ProjectAppBar size */}
      <div className="flex flex-row h-full">
        <DomainSection
          open={keywordTableState.tableUIState.showDomainsPanel}
          project={project}
          reload={reload}
          columnPickerBag={keywordTableState.columnPickerBag}
          keywordTableState={keywordTableState}
        />
        {loaded && selectedKeywordStats.result && drawerOpen && (
          <>
            {keywordTableState.tableUIState.showTagPanel && (
              <VoxelVerticalDrawer
                open={keywordTableState.tableUIState.showTagPanel}
                openWidthClass="w-80"
                openMinWidthClass="min-w-80"
                shadowClass="shadow-none"
                zIndexClass="z-20"
                bgColorClass="bg-white"
              >
                <TagSectionBeta
                  project={project}
                  reload={combinedReload}
                  keywordTableState={keywordTableState}
                  tagGroupStats={tagGroupStats.result}
                />
              </VoxelVerticalDrawer>
            )}
          </>
        )}
        <div className={`flex-grow h-full mx-auto px-4`}>
          <KeywordTable
            drawerOpen={drawerOpen}
            user={user}
            requests={apiCollections}
            project={project}
            totalCount={totalCount}
            totalVolume={totalVolume}
            currentPage={page}
            keywordTableState={keywordTableState}
            includeExportMonths={true}
            reloadKeywords={combinedReload}
            data={keywords}
            tagGroups={project.tagGroups}
            onDownload={handleOpenDownloads}
            changeVisibleColumns={true}
            page="dm"
            selectedKeywordStats={selectedKeywordStats?.result}
            rankCtrValues={ctrValueDictionary}
            setOpenRanksDialog={setOpenGetRanksDialog}
            setOpenAddDomainDialog={setAddDomainOpen}
            openGetRanksDialog={openGetRanksDialog}
          />
        </div>
        {loaded && conflictsTagGroup !== null && (
          <ConflictsDialog
            openDialog={conflictsTagGroup !== null}
            setOpenDialog={setConflictsTagGroup}
            project={project}
            tagGroup={conflictsTagGroup}
            reload={combinedReload}
          />
        )}
        {addDomainOpen !== null && addDomainOpen === DomainTypeGq.COMPETITOR && (
          <EditCompetitorsForm
            project={project}
            onSuccess={() => reload().then(() => setAddDomainOpen(null))}
            handleClose={() => setAddDomainOpen(null)}
          />
        )}
        {addDomainOpen !== null && addDomainOpen === DomainTypeGq.PROJECT && (
          <AddProjectDomainDialog
            project={project}
            rankDataNext={openGetRanksDialog}
            onSuccess={(domainId) =>
              reload()
                .then(() => {
                  keywordTableState.columnPickerBag.showProjectDomain(domainId);
                })
                .finally(() => {
                  setAddDomainOpen(null);
                })
            }
            close={() => {
              setAddDomainOpen(null);
              setOpenGetRanksDialog(false);
            }}
          />
        )}
        {openGetRanksDialog && !addDomainOpen && (
          <GetRankDataDialog
            project={project}
            open={openGetRanksDialog && !addDomainOpen}
            setOpen={setOpenGetRanksDialog}
            filterState={keywordTableState.backendFilterState}
            selectionState={keywordTableState.backendSelectionState}
            resetSelection={keywordTableState.handleResetSelection}
            showProjectDomain={keywordTableState.columnPickerBag.showProjectDomain}
            selectedKeywordStats={selectedKeywordStats?.result}
            reload={combinedReload}
            user={user}
          />
        )}
      </div>
    </div>
  );
};

export default DataManagementPage;
